import React, { useEffect, useRef } from "react";
import ModalPortal from "blocks/modal-manager/modal-portal";
import ModalWrapper from "blocks/modal-manager/modal-wrapper";
import useModal from "hooks/use-modal";
import { trackModalShown, trackModalClosed } from "utils/posthogTracking";

import ModalPaymentDetails from "blocks/modals/modal-payment-details";
import ModalMakePayment from "blocks/modals/modal-make-payment";
import ModalNewAccount from "blocks/modals/modal-new-account";
import ModalCreditorInfo from "blocks/modals/modal-creditor-info";
import ModalOffer from "blocks/modals/modal-offer";
import ModalOfferAccept from "blocks/modals/modal-offer-accept";
import ModalOfferDeclined from "blocks/modals/modal-offer-declined";
import ModalAccount from "blocks/modals/modal-account";
import ModalGeneralInfo from "blocks/modals/modal-general-info";
import ModalPayment from "blocks/modals/modal-payment";
import ModalAddMethod from "blocks/modals/modal-add-method";
import ModalSelectAgency from "blocks/modals/modal-select-agency";
import ModalVerification from "blocks/modals/modal-verification";
import ModalTermsScroller from "blocks/modals/modal-terms-scroller";
import ModalAlertSetupPayment from "../modals/modal-alert-setuppayment";
import ModalAlertOffer from "../modals/modal-alert-offer";
import ModalPaymentFailed from "../modals/modal-alert-paymentfailed";
import ModalEmailOptOut from "../modals/modal-email-optout";
import ModalQuestionComment from "../modals/modal-question-comment";
import Modal2FAVerification from "../modals/modal-2fa-verification";
import ModalConfirmDeletePayment from "../modals/modal-confirm-delete-payment";
import ModalConfirmCancelScheduled from "../modals/modal-confirm-cancel-scheduled";
import ModalSettlementAccepted from "../modals/modal-settlement-accepted";
import ModalSettlementDeclined from "../modals/modal-settlement-declined";
import ModalPaymentDeclined from "../modals/modal-payment-declined";

export const modalTypes = {
  ModalPaymentDetails: "ModalPaymentDetails",
  ModalMakePayment: "ModalMakePayment",
  ModalNewAccount: "ModalNewAccount",
  ModalCreditorInfo: "ModalCreditorInfo",
  ModalEmailOptOut: "ModalEmailOptOut",
  ModalOffer: "ModalOffer",
  ModalOfferAccept: "ModalOfferAccept",
  ModalOfferDeclined: "ModalOfferDeclined",
  ModalAccount: "ModalAccount",
  ModalGeneralInfo: "ModalGeneralInfo",
  ModalPayment: "ModalPayment",
  ModalAddMethod: "ModalAddMethod",
  ModalSelectAgency: "ModalSelectAgency",
  ModalTermsScroller: "ModalTermsScroller",
  Modal2FAVerification: "Modal2FAVerification",
  ModalVerification: "ModalVerification",
  ModalAlertSetupPayment: "ModalAlertSetupPayment",
  ModalAlertOffer: "ModalAlertOffer",
  ModalPaymentFailed: "ModalPaymentFailed",
  ModalQuestionComment: "ModalQuestionComment",
  ModalConfirmDeletePayment: "ModalConfirmDeletePayment",
  ModalConfirmCancelScheduled: "ModalConfirmCancelScheduled",
  ModalSettlementAccepted: "ModalSettlementAccepted",
  ModalSettlementDeclined: "ModalSettlementDeclined",
  ModalPaymentDeclined: "ModalPaymentDeclined",
};

const modals = {
  [modalTypes.ModalPaymentDetails]: ModalPaymentDetails,
  [modalTypes.ModalMakePayment]: ModalMakePayment,
  [modalTypes.ModalNewAccount]: ModalNewAccount,
  [modalTypes.ModalCreditorInfo]: ModalCreditorInfo,
  [modalTypes.ModalEmailOptOut]: ModalEmailOptOut,
  [modalTypes.ModalOffer]: ModalOffer,
  [modalTypes.ModalOfferAccept]: ModalOfferAccept,
  [modalTypes.ModalOfferDeclined]: ModalOfferDeclined,
  [modalTypes.ModalAccount]: ModalAccount,
  [modalTypes.ModalGeneralInfo]: ModalGeneralInfo,
  [modalTypes.ModalPayment]: ModalPayment,
  [modalTypes.ModalAddMethod]: ModalAddMethod,
  [modalTypes.ModalSelectAgency]: ModalSelectAgency,
  [modalTypes.ModalTermsScroller]: ModalTermsScroller,
  [modalTypes.ModalVerification]: ModalVerification,
  [modalTypes.Modal2FAVerification]: Modal2FAVerification,
  [modalTypes.ModalAlertSetupPayment]: ModalAlertSetupPayment,
  [modalTypes.ModalAlertOffer]: ModalAlertOffer,
  [modalTypes.ModalPaymentFailed]: ModalPaymentFailed,
  [modalTypes.ModalQuestionComment]: ModalQuestionComment,
  [modalTypes.ModalConfirmDeletePayment]: ModalConfirmDeletePayment,
  [modalTypes.ModalConfirmCancelScheduled]: ModalConfirmCancelScheduled,
  [modalTypes.ModalSettlementAccepted]: ModalSettlementAccepted,
  [modalTypes.ModalSettlementDeclined]: ModalSettlementDeclined,
  [modalTypes.ModalPaymentDeclined]: ModalPaymentDeclined,
};

const ModalManager = () => {
  const {
    activeModal,
    isOpen,
    modalProps,
    closeModal,
    setActiveModal,
    setModalProps,
  } = useModal();
  
  const modalOpenTimeRef = useRef(null);
  const previousModalRef = useRef(null);

  useEffect(() => {
    if (isOpen && activeModal) {
      // Track modal shown event
      modalOpenTimeRef.current = Date.now();
      previousModalRef.current = activeModal;
      trackModalShown(activeModal);
    } else if (!isOpen && previousModalRef.current && modalOpenTimeRef.current) {
      // Track modal closed event with duration
      const duration = Date.now() - modalOpenTimeRef.current;
      trackModalClosed(previousModalRef.current, duration, "user_action");
      modalOpenTimeRef.current = null;
      previousModalRef.current = null;
    }
  }, [isOpen, activeModal]);

  const ModalComponent = modals[activeModal];

  if (!isOpen) {
    return null;
  }

  let className;
  if (modalProps && modalProps.className) {
    className = modalProps.className;
  }

  const enhancedCloseModal = () => {
    if (modalOpenTimeRef.current && activeModal) {
      const duration = Date.now() - modalOpenTimeRef.current;
      trackModalClosed(activeModal, duration, "backdrop_click");
    }
    closeModal();
  };

  return (
    <ModalPortal>
      <ModalWrapper
        className={className}
        disableBackdropClose={activeModal === modalTypes.ModalVerification}
        {...{
          setActiveModal,
          setModalProps,
          closeModal: enhancedCloseModal,
        }}
      >
        <ModalComponent
          {...{
            isOpen,
            modalProps,
            setActiveModal,
            setModalProps,
            closeModal: enhancedCloseModal,
          }}
        />
      </ModalWrapper>
    </ModalPortal>
  );
};

export default ModalManager;
